import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, AlertCircle, Users, Mail, ExternalLink } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';

const TeamInfoStep = ({ formData, updateFormData, errors, setErrors }) => {
  const [teamMembers, setTeamMembers] = useState(formData.team_members || []);

  // Social media platforms
  const socialPlatforms = [
    { value: 'linkedin', label: 'LinkedIn', baseUrl: 'https://linkedin.com/in/', icon: '💼' },
    { value: 'X', label: 'Twitter', baseUrl: 'https://X.com/', icon: '🐦' },
    { value: 'github', label: 'GitHub', baseUrl: 'https://github.com/', icon: '🐙' },
    { value: 'facebook', label: 'Facebook', baseUrl: 'https://facebook.com/', icon: '📘' }
  ];

  // Update parent form data whenever team members change
  useEffect(() => {
    updateFormData({ team_members: teamMembers });
  }, [teamMembers, updateFormData]);

  const addTeamMember = () => {
    if (teamMembers.length < 10) {
      const newMember = {
        name: '',
        email: '',
        social_link: ''
      };
      setTeamMembers([...teamMembers, newMember]);
    }
  };

  const removeTeamMember = (index) => {
    const newTeamMembers = teamMembers.filter((_, i) => i !== index);
    setTeamMembers(newTeamMembers);
  };

  const updateTeamMember = (index, field, value) => {
    const newTeamMembers = [...teamMembers];
    newTeamMembers[index] = { ...newTeamMembers[index], [field]: value };
    setTeamMembers(newTeamMembers);
  };

  const validateTeamMember = (member, index) => {
    const newErrors = { ...errors };
    const errorKey = `team_member_${index}`;

    // Name validation
    if (!member.name) {
      newErrors[errorKey] = 'Team member name is required';
    } else if (!/^[a-zA-Z\s\-']+$/.test(member.name)) {
      newErrors[errorKey] = 'Name contains invalid characters';
    } else if (member.name.length > 100) {
      newErrors[errorKey] = 'Name is too long';
    }
    // Email validation
    else if (!member.email) {
      newErrors[errorKey] = 'Team member email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)) {
      newErrors[errorKey] = 'Please enter a valid email address';
    }
    // Social link validation
    else if (!member.social_link) {
      newErrors[errorKey] = 'Social media link is required';
    } else if (!/^https?:\/\/(www\.)?(linkedin|twitter|github|facebook)\.com\//.test(member.social_link)) {
      newErrors[errorKey] = 'Must be a valid LinkedIn, Twitter, GitHub, or Facebook URL';
    } else {
      delete newErrors[errorKey];
    }

    setErrors(newErrors);
  };

  const getSocialPlatform = (url) => {
    if (url.includes('linkedin.com')) return socialPlatforms.find(p => p.value === 'linkedin');
    if (url.includes('twitter.com')) return socialPlatforms.find(p => p.value === 'twitter');
    if (url.includes('github.com')) return socialPlatforms.find(p => p.value === 'github');
    if (url.includes('facebook.com')) return socialPlatforms.find(p => p.value === 'facebook');
    return null;
  };

  return (
    <div className="space-y-6 max-w-full overflow-hidden px-1 sm:px-0">
      {/* Header */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            👥 Team Information
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <p className="text-sm sm:text-p2-r text-neutral-600">
            Introduce your team members to build trust with potential donors. Include key people working on this project.
          </p>
        </CardContent>
      </Card>

      {/* Team Members */}
      <AnimatePresence>
        {teamMembers.map((member, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3 border-b border-neutral-100/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base sm:text-p1-m font-heading text-neutral-900 flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary-teal" />
                    Team Member {index + 1}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTeamMember(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
                  >
                    <Trash2 className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Remove</span>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-p2-m text-neutral-900 mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    value={member.name}
                    onChange={(e) => updateTeamMember(index, 'name', e.target.value)}
                    onBlur={() => validateTeamMember(member, index)}
                    placeholder="e.g., John Doe"
                    maxLength={100}
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-p2-m text-neutral-900 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                    <Input
                      type="email"
                      value={member.email}
                      onChange={(e) => updateTeamMember(index, 'email', e.target.value)}
                      onBlur={() => validateTeamMember(member, index)}
                      placeholder="john@example.com"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Social Media Link */}
                <div>
                  <label className="block text-p2-m text-neutral-900 mb-2">
                    Social Media Profile *
                  </label>
                  <div className="relative">
                    <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                    <Input
                      type="url"
                      value={member.social_link}
                      onChange={(e) => updateTeamMember(index, 'social_link', e.target.value)}
                      onBlur={() => validateTeamMember(member, index)}
                      placeholder="https://linkedin.com/in/johndoe"
                      className="pl-10"
                    />
                    {member.social_link && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                        onClick={() => window.open(member.social_link, '_blank')}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Platform indicator */}
                  {member.social_link && (
                    <div className="mt-2">
                      {(() => {
                        const platform = getSocialPlatform(member.social_link);
                        return platform ? (
                          <div className="flex items-center gap-2 text-p2-r text-neutral-600">
                            <span>{platform.icon}</span>
                            <span>{platform.label} Profile</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {/* Validation error for this team member */}
                  {errors[`team_member_${index}`] && (
                    <div className="flex items-center gap-1 text-red-600 mt-2">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-p2-r">{errors[`team_member_${index}`]}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Add Team Member Button */}
      {teamMembers.length < 10 && (
        <Card className="border-2 border-dashed border-neutral-300 hover:border-primary-teal transition-all duration-300">
          <CardContent className="p-6 text-center">
            <Button
              variant="outline"
              onClick={addTeamMember}
              className="flex items-center gap-2 mx-auto"
            >
              <Plus className="w-4 h-4" />
              Add Team Member
            </Button>
            <p className="text-p2-r text-neutral-600 mt-2">
              Add up to {10 - teamMembers.length} more team {teamMembers.length === 9 ? 'member' : 'members'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Social Platform Guide */}
      {teamMembers.length === 0 && (
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="text-p1-m text-neutral-900">
              Supported Social Platforms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {socialPlatforms.map((platform) => (
                <div key={platform.value} className="text-center p-3 border border-neutral-200 rounded-lg">
                  <div className="text-2xl mb-2">{platform.icon}</div>
                  <p className="text-p2-m text-neutral-900">{platform.label}</p>
                  <p className="text-p2-r text-neutral-600 text-xs mt-1">
                    {platform.baseUrl}...
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tips */}
      <Card className="bg-purple-50 border-purple-200 border-2">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div className="text-p2-r text-purple-800">
              <p className="font-semibold mb-1">Team Tips</p>
              <ul className="space-y-1 text-sm">
                <li>• Include key team members and their roles</li>
                <li>• Use professional social media profiles</li>
                <li>• LinkedIn profiles work best for credibility</li>
                <li>• Team information builds donor confidence</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamInfoStep;
